import { ReactNode } from "react";

import { Link } from "react-router";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Container } from "@/components/pieces/bootstrap";
import {
  StyledSmallText,
  StyledSubtitle,
  StyledTitle,
} from "@/components/pieces/marketing/Titles";
import { Button } from "@/components/ui/button";
import { PUBLIC_CONTENT_MAX_WIDTH } from "@/css/constants";

interface ExperienceItem {
  title: string | ReactNode;
  photo: string;
  feature?: string | ReactNode;
}

interface UserExperienceV2Props {
  items?: ExperienceItem[];
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  ctaLink?: string | null;
  ctaText?: string | null;
  onCtaClick?: (() => void) | null;
  smallText?: string | null;
  itemsPerRow?: number;
  background?: string;
  footer?: ReactNode | null;
  borderWidth?: number;
}

export function UserExperienceV2({
  items = [],
  title = "A seamless, modern experience",
  subtitle = "World-class coaches. Incredible technology. Lasting change.",
  ctaLink,
  ctaText,
  onCtaClick,
  smallText,
  itemsPerRow = 4,
  background = "#FFF",
  footer = null,
  borderWidth = 1,
}: UserExperienceV2Props) {
  // Grid layout mapping based on itemsPerRow
  const gridLayoutMap = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-4",
  };

  const gridCols =
    gridLayoutMap[itemsPerRow as keyof typeof gridLayoutMap] ||
    gridLayoutMap[1];

  const renderHeader = () => (
    <div className="mb-12">
      {smallText && <StyledSmallText>{smallText}</StyledSmallText>}
      <StyledTitle>{title}</StyledTitle>
      <StyledSubtitle>{subtitle}</StyledSubtitle>
    </div>
  );

  const renderGridItem = (item: ExperienceItem, index: number) => (
    <div
      className="flex min-h-48 flex-col items-center overflow-hidden px-5 py-9 text-center"
      key={`experience-item-${index}`}
      style={{ backgroundColor: background }}
    >
      <EmbedSvg src={item.photo} className="mb-5 max-h-[100px]" />
      <div className="flex flex-col">
        {item.feature && (
          <div className="mb-2 text-[20px] font-semibold text-gray-800">
            {item.feature}
          </div>
        )}
        <div className="text-lg">{item.title}</div>
      </div>
    </div>
  );

  const renderCta = () => {
    if (!ctaText) return null;

    return (
      <div className="mt-5 flex justify-center">
        {onCtaClick ? (
          <Button size="xl" className="w-[300px]" onClick={onCtaClick}>
            {ctaText}
          </Button>
        ) : (
          <Button size="xl" className="w-[300px]" asChild>
            <Link to={ctaLink || "#"}>{ctaText}</Link>
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="py-24" style={{ backgroundColor: background }}>
      <Container maxWidth={PUBLIC_CONTENT_MAX_WIDTH}>
        {renderHeader()}

        <div
          className={`overflow-hidden rounded-lg ${borderWidth > 0 ? "border" : ""}`}
        >
          <div className={`grid gap-[1px] bg-gray-200 ${gridCols}`}>
            {items.map(renderGridItem)}
          </div>
        </div>

        {renderCta()}
        {footer}
      </Container>
    </div>
  );
}
